import { ItineraryItem, Location } from '../types';

/**
 * Calculates the Great Circle distance between two points in kilometers.
 * Uses the Haversine formula.
 */
export const calculateDistance = (loc1: Location, loc2: Location): number => {
    const R = 6371; // Earth's radius in km
    const dLat = toRad(loc2.coordinates.lat - loc1.coordinates.lat);
    const dLng = toRad(loc2.coordinates.lng - loc1.coordinates.lng);
    const lat1 = toRad(loc1.coordinates.lat);
    const lat2 = toRad(loc2.coordinates.lat);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const toRad = (value: number) => (value * Math.PI) / 180;

/**
 * Optimizes the order of itinerary items based on the shortest path (Greedy Nearest Neighbor).
 * @param items List of itinerary items with locations.
 * @param startLocation Optional starting point (e.g., Airport).
 * @param endLocation Optional ending point (e.g., Airport for departure).
 */
export const optimizeItinerary = (
    items: ItineraryItem[],
    startLocation?: Location,
    endLocation?: Location
): ItineraryItem[] => {
    // Filter items that actually have a location
    const locations = items.filter(item => item.location && item.location.coordinates);
    const otherItems = items.filter(item => !item.location || !item.location.coordinates);

    if (locations.length < 3) return items; // Need at least 3 points to optimize meaningfully if start/end are fixed

    const optimized: ItineraryItem[] = [];
    // If startLocation is separate from items, we treat it as reference, but usually the first item IS the start in this context.
    // However, if we want to FIX the first and last items of the PROVIDED list:

    // Let's assume the component identifies specific tours as "Start" or "End" or we just fix the first and last of the current list if no explicit locations passed.
    // For this implementation, we will treat the User's "First Selected Tour" as Start and "Last Selected Tour" as End if they chose them in that order, 
    // OR more intelligently: We simply pick the Start/End from specific known locations if provided, 
    // BUT since we are reordering a list of tours, let's assume:
    // 1. Start is fixed (First item).
    // 2. End is fixed (Last item).
    // 3. Middle items are optimized.

    // However, the prompt implies adding an external "Airport" start.
    // For now, let's implement: Fix First Item, Fix Last Item (if requested), Optimize Middle.

    const startItem = locations[0];
    const endItem = locations[locations.length - 1]; // Assume last item is the intended destination/end

    // If we only have 2 points, nothing to optimize
    if (locations.length <= 2) return items;

    // Fixed Start
    optimized.push(startItem);

    // Middle Items to Optimize
    const middleItems = locations.slice(1, locations.length - 1);
    // Add the end item to the unvisited set? No, end is fixed.

    const unvisited = [...middleItems];
    let currentItem = startItem;

    // Greedy loop
    while (unvisited.length > 0) {
        let nearestIndex = -1;
        let minDist = Infinity;

        // eslint-disable-next-line no-loop-func
        unvisited.forEach((item, index) => {
            const d = calculateDistance(currentItem.location!, item.location!);
            // We could also consider distance to EndItem to guide it towards the destination?
            // "Greedy" just looks at next step. A* would look at End. 
            // For Safari, simple greedy nearest is usually fine for "TSP-like" path.
            if (d < minDist) {
                minDist = d;
                nearestIndex = index;
            }
        });

        if (nearestIndex !== -1) {
            currentItem = unvisited[nearestIndex];
            unvisited.splice(nearestIndex, 1);
            optimized.push(currentItem);
        } else {
            break;
        }
    }

    // Fixed End
    if (locations.length > 1) {
        optimized.push(endItem);
    }

    // Re-assign days sequentially
    const reordered = [...optimized, ...otherItems].map((item, index) => ({
        ...item,
        day: index + 1
    }));

    return reordered;
};
